import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';

const ImageAdjustmentDialog = ({
  open,
  onClose,
  imageUrl,
  currentConfig = {},
  onSave,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [config, setConfig] = useState({
    fit: currentConfig?.fit || 'contain',
    position: currentConfig?.position || 'center',
    positionX: 50,
    positionY: 50,
  });

  // Parse position string to extract X and Y
  useEffect(() => {
    if (currentConfig?.position) {
      const parts = currentConfig.position.split(' ').filter(p => p);
      if (parts.length === 2) {
        const xPercent = parseInt(parts[0]) || 50;
        const yPercent = parseInt(parts[1]) || 50;
        setConfig(prev => ({
          ...prev,
          positionX: xPercent,
          positionY: yPercent,
        }));
      }
    }
  }, [currentConfig?.position, open]);

  const handleFitChange = (e) => {
    setConfig({ ...config, fit: e.target.value });
  };

  const handlePositionXChange = (e, value) => {
    setConfig({ ...config, positionX: value });
  };

  const handlePositionYChange = (e, value) => {
    setConfig({ ...config, positionY: value });
  };

  const handleSave = () => {
    const positionString = `${config.positionX}% ${config.positionY}%`;
    onSave({
      fit: config.fit,
      position: positionString,
    });
  };

  const fitOptions = [
    { value: 'contain', label: 'Contain (Fit whole image)' },
    { value: 'cover', label: 'Cover (Fill frame, may crop)' },
    { value: 'fill', label: 'Fill (Stretch to fit)' },
    { value: 'scale-down', label: 'Scale Down (Smaller or same size)' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? 'sm' : 'md'}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Adjust Photo Position & Fit
      </DialogTitle>

      <DialogContent sx={{ pt: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 3 }}>
          {/* Preview Section */}
          <Box
            sx={{
              flex: isMobile ? '1 1 100%' : '1 1 45%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Preview
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: isMobile ? '200px' : '250px',
                backgroundColor: '#f5f5f5',
                border: '2px solid #e0e0e0',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: config.fit,
                    objectPosition: `${config.positionX}% ${config.positionY}%`,
                  }}
                />
              ) : (
                <Typography color="textSecondary">No image</Typography>
              )}
            </Box>
          </Box>

          {/* Controls Section */}
          <Box
            sx={{
              flex: isMobile ? '1 1 100%' : '1 1 55%',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {/* Fit Option Dropdown */}
            <FormControl fullWidth>
              <InputLabel id="fit-select-label">Image Fit</InputLabel>
              <Select
                labelId="fit-select-label"
                id="fit-select"
                value={config.fit}
                label="Image Fit"
                onChange={handleFitChange}
              >
                {fitOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Position X Slider */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Horizontal Position
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {config.positionX}%
                </Typography>
              </Box>
              <Slider
                min={0}
                max={100}
                step={5}
                value={config.positionX}
                onChange={handlePositionXChange}
                marks={[
                  { value: 0, label: 'Left' },
                  { value: 50, label: 'Center' },
                  { value: 100, label: 'Right' },
                ]}
                sx={{ mt: 2 }}
              />
            </Box>

            {/* Position Y Slider */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Vertical Position
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {config.positionY}%
                </Typography>
              </Box>
              <Slider
                min={0}
                max={100}
                step={5}
                value={config.positionY}
                onChange={handlePositionYChange}
                marks={[
                  { value: 0, label: 'Top' },
                  { value: 50, label: 'Middle' },
                  { value: 100, label: 'Bottom' },
                ]}
                sx={{ mt: 2 }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          fullWidth={isMobile}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          fullWidth={isMobile}
          variant="contained"
          color="primary"
          endIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Adjustments'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageAdjustmentDialog;
